/**
 * Ciclo de pagamento para o painel de custos da roteirização.
 * Espelho enxuto de api/utils/subsidioMotorCiclo.js + herança CONTROLE→OPERACIONAL.
 */

function parseDia(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return (
      value.getFullYear() +
      '-' +
      String(value.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(value.getDate()).padStart(2, '0')
    );
  }
  const s = String(value);
  return s.length >= 10 ? s.substring(0, 10) : null;
}

function parseDateParts(dia) {
  const parts = String(dia || '').split('-');
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return { y, m, d };
}

function formatDate(y, m, d) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function daysInMonth(y, m) {
  return new Date(y, m, 0).getDate();
}

function addDays(y, m, d, delta) {
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

function todayRef() {
  return parseDia(new Date());
}

function resolveCicloPorDuracao(contratoRow, parts, dia, duracaoDias, refSuffix) {
  const len = Number(duracaoDias);
  if (!Number.isFinite(len) || len < 1) {
    return {
      ref: `${dia.substring(0, 7)}-${refSuffix}`,
      inicio: formatDate(parts.y, parts.m, 1),
      fim: formatDate(parts.y, parts.m, daysInMonth(parts.y, parts.m)),
      dias: daysInMonth(parts.y, parts.m),
    };
  }
  const anchor = parseDia(contratoRow && contratoRow.dt_referencia_ciclo);
  if (!anchor) {
    return {
      ref: `${dia.substring(0, 7)}-${refSuffix}`,
      inicio: formatDate(parts.y, parts.m, 1),
      fim: formatDate(parts.y, parts.m, daysInMonth(parts.y, parts.m)),
      dias: daysInMonth(parts.y, parts.m),
    };
  }
  const ap = parseDateParts(anchor);
  if (!ap) return null;
  const anchorUtc = Date.UTC(ap.y, ap.m - 1, ap.d);
  const diaUtc = Date.UTC(parts.y, parts.m - 1, parts.d);
  let elapsed = Math.floor((diaUtc - anchorUtc) / 86400000);
  if (elapsed < 0) elapsed = 0;
  const cicloIdx = Math.floor(elapsed / len);
  const inicioParts = addDays(ap.y, ap.m, ap.d, cicloIdx * len);
  const fimParts = addDays(inicioParts.y, inicioParts.m, inicioParts.d, len - 1);
  const inicio = formatDate(inicioParts.y, inicioParts.m, inicioParts.d);
  const fim = formatDate(fimParts.y, fimParts.m, fimParts.d);
  return {
    ref: `${inicio}_${refSuffix}`,
    inicio,
    fim,
    dias: len,
  };
}

const DIARIO_DIAS_PADRAO = 1;
const SEMANAL_DIAS_PADRAO = 7;
const QUINZENAL_DIAS_PADRAO = 15;
const MENSAL_DIAS_PADRAO = 30;

function resolveCicloContrato(contratoRow, diaRef) {
  const dia = parseDia(diaRef) || todayRef();
  if (!dia) return null;
  const parts = parseDateParts(dia);
  if (!parts) return null;

  const tipo = String((contratoRow && contratoRow.tipo_ciclo_pagamento) || 'MENSAL').toUpperCase();

  // QUINZENAL: sempre 14 dias para escala de custos.
  if (tipo === 'QUINZENAL') {
    const resolved = resolveCicloPorDuracao(
      contratoRow,
      parts,
      dia,
      QUINZENAL_DIAS_PADRAO,
      'Q',
    );
    if (!resolved) return null;
    return {
      ...resolved,
      dias: QUINZENAL_DIAS_PADRAO,
      tipo: 'QUINZENAL',
    };
  }

  if (tipo === 'DIAS') {
    const nDias = Number(contratoRow && contratoRow.dias_ciclo_pagamento);
    return resolveCicloPorDuracao(contratoRow, parts, dia, nDias, 'D');
  }

  // MENSAL: sempre 30 dias para escala de custos, independente do mês calendário.
  const diaPag = Number(contratoRow && contratoRow.dia_ciclo_pagamento);
  if (!Number.isFinite(diaPag) || diaPag < 1 || diaPag > 28) {
    const inicio = formatDate(parts.y, parts.m, 1);
    const fimParts = addDays(parts.y, parts.m, 1, MENSAL_DIAS_PADRAO - 1);
    return {
      ref: inicio.substring(0, 7),
      inicio,
      fim: formatDate(fimParts.y, fimParts.m, fimParts.d),
      dias: MENSAL_DIAS_PADRAO,
      tipo: 'MENSAL',
    };
  }

  let inicioMes;
  if (parts.d >= diaPag) {
    inicioMes = { y: parts.y, m: parts.m, d: diaPag };
  } else {
    const prev = parts.m === 1 ? { y: parts.y - 1, m: 12 } : { y: parts.y, m: parts.m - 1 };
    inicioMes = { y: prev.y, m: prev.m, d: diaPag };
  }
  const fimMes = addDays(inicioMes.y, inicioMes.m, inicioMes.d, MENSAL_DIAS_PADRAO - 1);
  const cicloInicio = formatDate(inicioMes.y, inicioMes.m, inicioMes.d);
  const cicloFim = formatDate(fimMes.y, fimMes.m, fimMes.d);
  return {
    ref: cicloInicio,
    inicio: cicloInicio,
    fim: cicloFim,
    dias: MENSAL_DIAS_PADRAO,
    tipo: 'MENSAL',
  };
}

function diasNoCiclo(contratoRow, diaRef) {
  const ciclo = resolveCicloContrato(contratoRow, diaRef);
  return ciclo && ciclo.dias ? ciclo.dias : null;
}

function hasOwnCiclo(row) {
  if (!row) return false;
  const tipo = row.tipo_ciclo_pagamento;
  return tipo != null && String(tipo).trim() !== '';
}

function controleRowFromOperacional(opRow) {
  if (!opRow) return null;
  return {
    id_contrato: opRow.id_contrato_controle,
    cod_contrato: opRow.cod_contrato_controle,
    descricao: opRow.descricao_controle,
    tipo_ciclo_pagamento: opRow.tipo_ciclo_pagamento_controle,
    dia_ciclo_pagamento: opRow.dia_ciclo_pagamento_controle,
    dias_ciclo_pagamento: opRow.dias_ciclo_pagamento_controle,
    dt_referencia_ciclo: opRow.dt_referencia_ciclo_controle,
  };
}

/**
 * Ciclo efetivo: filho se tiver tipo_ciclo; senão herda do CONTROLE.
 */
function resolveCicloEfetivo(operacionalRow, controleRow, diaRef) {
  const parent = controleRow || controleRowFromOperacional(operacionalRow);
  const source = hasOwnCiclo(operacionalRow) ? operacionalRow : parent;
  if (!source || !hasOwnCiclo(source)) {
    const fallback = resolveCicloContrato({ tipo_ciclo_pagamento: 'MENSAL' }, diaRef);
    return fallback
      ? {
          ...fallback,
          tipo: 'MENSAL',
          id_contrato: operacionalRow && operacionalRow.id_contrato,
          cod_contrato: operacionalRow && operacionalRow.cod_contrato,
          inherited: true,
        }
      : null;
  }
  const resolved = resolveCicloContrato(source, diaRef);
  if (!resolved) return null;
  const tipo = String(source.tipo_ciclo_pagamento || 'MENSAL').toUpperCase();
  return {
    ...resolved,
    tipo,
    id_contrato: operacionalRow && operacionalRow.id_contrato,
    cod_contrato: operacionalRow && operacionalRow.cod_contrato,
    inherited: !hasOwnCiclo(operacionalRow),
  };
}

function diarioDiasPadrao() {
  return DIARIO_DIAS_PADRAO;
}

function semanalDiasPadrao() {
  return SEMANAL_DIAS_PADRAO;
}

function quinzenalDiasPadrao() {
  return QUINZENAL_DIAS_PADRAO;
}

function mensalDiasPadrao() {
  return MENSAL_DIAS_PADRAO;
}

/** Chaves de dias da semana (segunda = início da semana operacional). */
const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/** Mapeia chave i18n do dia → campo `valido_*` do quadro de horários. */
const WEEKDAY_TO_VALIDO_FIELD = {
  monday: 'valido_segunda',
  tuesday: 'valido_terca',
  wednesday: 'valido_quarta',
  thursday: 'valido_quinta',
  friday: 'valido_sexta',
  saturday: 'valido_sabado',
  sunday: 'valido_domingo',
};

/**
 * Converte seleção de dias (chaves WEEKDAY_KEYS) em flags 0/1 do quadro.
 * Garante ao menos um dia marcado (segunda, se a lista vier vazia).
 * @param {string[]} selectedKeys
 * @returns {{ valido_segunda: number, valido_terca: number, valido_quarta: number, valido_quinta: number, valido_sexta: number, valido_sabado: number, valido_domingo: number }}
 */
function weekdaysToValidoFlags(selectedKeys) {
  const selected = new Set(
    (Array.isArray(selectedKeys) ? selectedKeys : [])
      .filter((k) => WEEKDAY_KEYS.includes(k)),
  );
  if (!selected.size) {
    selected.add(WEEKDAY_KEYS[0]);
  }
  const flags = {};
  WEEKDAY_KEYS.forEach((key) => {
    const field = WEEKDAY_TO_VALIDO_FIELD[key];
    flags[field] = selected.has(key) ? 1 : 0;
  });
  return flags;
}

/**
 * Escala dias de calendário pelos dias operacionais da semana.
 * Ex.: mensal (30) × seg–sex (5/7) → ceil(21.43) = 22.
 * @param {number} baseDays
 * @param {number} weekdayCount - quantidade de dias selecionados (1–7)
 * @returns {number}
 */
function scaleDaysByWeekdays(baseDays, weekdayCount) {
  const base = Number(baseDays);
  const count = Number(weekdayCount);
  if (!Number.isFinite(base) || base <= 0) return 0;
  if (!Number.isFinite(count) || count <= 0) return Math.max(1, Math.ceil(base));
  if (count >= 7) return base;
  return Math.max(1, Math.ceil((base * count) / 7));
}

/**
 * @param {Array} ciclosEfetivos - resultados de resolveCicloEfetivo
 * @param {string} [diaRef]
 * @param {Function} t - i18n function (key, values?) => string
 * @param {number} [weekdayCount=7] - dias da semana operacionais (segunda=início)
 */
function buildCicloFilterOptions(ciclosEfetivos, diaRef, t, weekdayCount) {
  const translate = typeof t === 'function' ? t : (k, v) => {
    if (k === 'costCycleContractDays' && v && v.days != null) return `${v.days} dias`;
    return k;
  };
  const list = (ciclosEfetivos || []).filter((c) => c && Number.isFinite(Number(c.dias)) && Number(c.dias) > 0);
  if (!list.length) {
    return { options: [], maxDias: 0, defaultKey: null };
  }

  const maxDias = Math.max(...list.map((c) => Number(c.dias)));
  const daysPerWeek = Number.isFinite(Number(weekdayCount)) && Number(weekdayCount) > 0
    ? Math.min(7, Math.max(1, Math.round(Number(weekdayCount))))
    : 7;

  const presets = [
    { key: 'diario', dias: DIARIO_DIAS_PADRAO, labelKey: 'costCycleDaily' },
    { key: 'semanal', dias: SEMANAL_DIAS_PADRAO, labelKey: 'costCycleWeekly' },
    { key: 'quinzenal', dias: QUINZENAL_DIAS_PADRAO, labelKey: 'costCycleBiweekly' },
    { key: 'mensal', dias: MENSAL_DIAS_PADRAO, labelKey: 'costCycleMonthly' },
  ];

  const options = [];
  const coveredDays = new Set();

  presets.forEach((p) => {
    if (p.dias <= maxDias) {
      const dias = scaleDaysByWeekdays(p.dias, daysPerWeek);
      const baseText = translate(`routePlanning.sidebar.${p.labelKey}`);
      options.push({
        value: p.key,
        text: daysPerWeek < 7
          ? translate('routePlanning.sidebar.costCyclePeriodWithDays', {
            period: baseText,
            days: dias,
          })
          : baseText,
        dias,
      });
      coveredDays.add(dias);
    }
  });

  const uniqueExtraDays = [...new Set(list.map((c) => Number(c.dias)))]
    .filter((d) => d > 0 && d <= maxDias)
    .map((d) => scaleDaysByWeekdays(d, daysPerWeek))
    .filter((d) => d > 0 && !coveredDays.has(d))
    .sort((a, b) => a - b);

  uniqueExtraDays.forEach((d) => {
    options.push({
      value: `dias:${d}`,
      text: translate('routePlanning.sidebar.costCycleContractDays', { days: d }),
      dias: d,
    });
    coveredDays.add(d);
  });

  options.sort((a, b) => a.dias - b.dias);

  const defaultOpt = options.reduce((best, cur) => {
    if (!best || cur.dias > best.dias) return cur;
    return best;
  }, null);

  return {
    options,
    maxDias,
    defaultKey: defaultOpt ? defaultOpt.value : null,
  };
}

function resolveSelectedDays(options, selectedKey) {
  const opt = (options || []).find((o) => o && String(o.value) === String(selectedKey));
  return opt && Number.isFinite(Number(opt.dias)) ? Number(opt.dias) : null;
}

/**
 * Fator de escala: valor × (período / ciclo).
 * @param {number} diasPeriodo - dias do filtro de análise (30, 14, 7, 1, …)
 * @param {number} diasCiclo - dias do ciclo de pagamento de referência
 * @returns {number} diasPeriodo / diasCiclo, ou 1 se inválido
 */
function costScaleFactor(diasPeriodo, diasCiclo) {
  const periodo = Number(diasPeriodo);
  const ciclo = Number(diasCiclo);
  if (!Number.isFinite(periodo) || periodo <= 0 || !Number.isFinite(ciclo) || ciclo <= 0) return 1;
  return periodo / ciclo;
}

/** Arredonda para 2 casas (mesmo critério da tela: km e moeda). */
function round2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export {
  parseDia,
  todayRef,
  resolveCicloContrato,
  diasNoCiclo,
  resolveCicloEfetivo,
  controleRowFromOperacional,
  diarioDiasPadrao,
  semanalDiasPadrao,
  quinzenalDiasPadrao,
  mensalDiasPadrao,
  DIARIO_DIAS_PADRAO,
  SEMANAL_DIAS_PADRAO,
  QUINZENAL_DIAS_PADRAO,
  MENSAL_DIAS_PADRAO,
  WEEKDAY_KEYS,
  WEEKDAY_TO_VALIDO_FIELD,
  weekdaysToValidoFlags,
  scaleDaysByWeekdays,
  buildCicloFilterOptions,
  resolveSelectedDays,
  costScaleFactor,
  round2,
};
